import { useState, type FormEvent } from "react";
import { AuthShell, CardThemeToggle } from "./AuthShell";
import { NetIndicator } from "./NetIndicator";
import { apiUrl } from "../../utils/jwt-storage";
import { spaNavigate } from "../../utils/navigation";

/**
 * React port of ``users/templates/users/new-ui/forgot_password.html``.
 *
 * Anonymous email-entry page. POSTs ``{email}`` to
 * ``/api/auth/forgot-password/`` which always returns 200 for anti-
 * enumeration reasons (see ``users/password_reset.py::api_forgot_password``).
 * We show a generic confirmation regardless of whether the email
 * actually resolves to an account.
 *
 * The 503 branch is offline-specific (desktop-embedded Django can't
 * reach the cloud's SMTP → returns ``{code: 'offline'}`` so we don't
 * lie to the user with a "sent" confirmation).
 */
export const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<{ text: string; kind: "success" | "error" | "offline" } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    setStatus(null);
    setSubmitting(true);
    try {
      const res = await fetch(apiUrl("/api/auth/forgot-password/"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      if (res.status === 200) {
        setStatus({
          text: "If an account exists for that email, a password-reset link is on its way.",
          kind: "success",
        });
        return;
      }
      if (res.status === 503 && body.code === "offline") {
        setStatus({
          text: (body.error as string) || "You're offline. Reset emails send from the cloud, reconnect and try again.",
          kind: "offline",
        });
        return;
      }
      setStatus({
        text: (body.error as string) || `Request failed (HTTP ${res.status}). Try again in a moment.`,
        kind: "error",
      });
    } catch {
      setStatus({
        text: "Network error. Try again in a moment.",
        kind: "offline",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const statusColor =
    status?.kind === "success"
      ? "var(--color-positive-content, #047857)"
      : status?.kind === "offline"
        ? "var(--color-warning-content, #92571c)"
        : "var(--color-negative-content, #b91c1c)";

  return (
    <AuthShell>
      <div className="form-wrapper">
        <div className="form-wrapper-header">
          <h2>Forgot password</h2>
          <CardThemeToggle />
          <NetIndicator />
        </div>
        <p style={{ color: "var(--color-neutral-content-subtler, #4b5563)" }}>
          Enter the email on your account and we'll send you a link to set a new password.
        </p>
        <form onSubmit={onSubmit}>
          <div className="input-wrapper">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              inputMode="email"
              className="lsf-input-ls"
              name="email"
              id="email"
              autoComplete="email"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          {status && (
            <p
              style={{
                color: statusColor,
                margin: "8px 0",
                fontSize: "var(--font-size-body-small)",
                lineHeight: 1.4,
              }}
            >
              {status.text}
            </p>
          )}
          <button type="submit" aria-label="Send reset link" className="login-button" disabled={submitting}>
            {submitting ? "Sending…" : "Send reset link"}
          </button>
        </form>
        <div className="text-wrapper" style={{ marginTop: 12 }}>
          <button
            type="button"
            onClick={() => spaNavigate("/auth/login", true)}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              color: "var(--color-primary-content, #1e40af)",
              textDecoration: "underline",
              cursor: "pointer",
              font: "inherit",
            }}
          >
            ← Back to sign in
          </button>
        </div>
      </div>
    </AuthShell>
  );
};

export default ForgotPassword;
